import { cn } from "@/lib/utils";

interface LogoProps {
  size?: "sm" | "md" | "lg" | "xl";
  showText?: boolean;
  className?: string;
  variant?: "full" | "icon-only" | "text-only";
  theme?: "light" | "dark" | "auto";
}

const sizeClasses = {
  sm: {
    container: "w-8 h-8",
    text: "text-lg",
  },
  md: {
    container: "w-12 h-12",
    text: "text-xl",
  },
  lg: {
    container: "w-16 h-16",
    text: "text-2xl",
  },
  xl: {
    container: "w-20 h-20",
    text: "text-3xl",
  },
};

export function Logo({ 
  size = "md", 
  showText = true, 
  className, 
  variant = "full",
  theme = "auto"
}: LogoProps) {
  const sizeClass = sizeClasses[size];
  
  // Use dark colors for light backgrounds, light colors for dark backgrounds
  const isDarkTheme = theme === "dark";
  const logoColor = isDarkTheme ? "#374151" : "#f8fafc";

  const IconComponent = () => (
    <div className={cn("relative flex items-center justify-center", sizeClass.container)}>
      {/* Geometric G exactly like reference - arrow pointing left with opening */}
      <svg
        viewBox="0 0 100 100"
        className="w-full h-full"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M15 15
             L85 15
             L85 45
             L55 45
             L55 35
             L75 35
             L75 25
             L25 25
             L25 75
             L75 75
             L75 65
             L55 65
             L55 55
             L85 55
             L85 85
             L15 85
             L15 15 Z"
          fill={logoColor}
        />
      </svg>
    </div>
  );

  const TextComponent = () => (
    <span className={cn(
      "font-bold transition-all duration-300",
      sizeClass.text,
      isDarkTheme ? "text-gray-700" : "text-gray-100"
    )}>
      GoalDigger
    </span>
  );

  if (variant === "icon-only") {
    return (
      <div className={cn("flex items-center justify-center", className)}>
        <IconComponent />
      </div>
    );
  }

  if (variant === "text-only") {
    return (
      <div className={cn("flex items-center justify-center", className)}>
        <TextComponent />
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <IconComponent />
      {showText && <TextComponent />}
    </div>
  );
}